import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, useParams } from 'react-router-dom';
import { AuthGuard } from '@/features/auth/components/AuthGuard';
import { OnboardingGuard } from '@/features/onboarding/components/OnboardingGuard';
import { AppLayout } from '@/shared/components/layout/AppLayout';
import { LoginPage } from '@/features/auth/pages/LoginPage';
import { PageLoader } from '@/shared/components/feedback/LoadingSpinner';
import { ErrorBoundary } from '@/shared/components/feedback/ErrorBoundary';
import { NotFoundPage } from '@/shared/components/feedback/NotFoundPage';
import { UlidRouteGuard } from '@/shared/components/feedback/UlidRouteGuard';

/** Redirects `/venues/:connectionId` → `/venue-connections/:connectionId`. */
function VenueConnectionParamRedirect() {
  const { connectionId } = useParams();
  return <Navigate to={`/venue-connections/${connectionId ?? ''}`} replace />;
}

// ── Public pages ─────────────────────────────────────────────────────────────
const LandingPage = lazy(() =>
  import('@/features/landing/pages/LandingPage').then((m) => ({ default: m.LandingPage })),
);
const DemoPage = lazy(() =>
  import('@/features/demo/pages/DemoPage').then((m) => ({ default: m.DemoPage })),
);
const ForgotPasswordPage = lazy(() =>
  import('@/features/auth/pages/ForgotPasswordPage').then((m) => ({
    default: m.ForgotPasswordPage,
  })),
);
const ResetPasswordPage = lazy(() =>
  import('@/features/auth/pages/ResetPasswordPage').then((m) => ({
    default: m.ResetPasswordPage,
  })),
);
const OnboardingPage = lazy(() =>
  import('@/features/onboarding/pages/OnboardingPage').then((m) => ({
    default: m.OnboardingPage,
  })),
);

// ── Protected pages ──────────────────────────────────────────────────────────
const DashboardPage = lazy(() =>
  import('@/features/dashboard/pages/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);

// Venue Connections (replaces exchange-accounts)
const VenueConnectionsPage = lazy(
  () => import('@/features/venue-connections/pages/VenueConnectionsPage'),
);
const VenueConnectionDetailPage = lazy(
  () => import('@/features/venue-connections/pages/VenueConnectionDetailPage'),
);
const VenueConnectWizardPage = lazy(
  () => import('@/features/venue-connections/pages/VenueConnectWizardPage'),
);

// Execution Profiles
const ExecutionProfilesPage = lazy(
  () => import('@/features/execution-profiles/pages/ExecutionProfilesPage'),
);
const ExecutionProfileDetailPage = lazy(
  () => import('@/features/execution-profiles/pages/ExecutionProfileDetailPage'),
);

// Bots
const BotListPage = lazy(() => import('@/features/bots/pages/BotListPage'));
const BotCreatePage = lazy(() => import('@/features/bots/pages/BotCreatePage'));
const BotDetailPage = lazy(() => import('@/features/bots/pages/BotDetailPage'));

// Strategies
const StrategyCatalogPage = lazy(() => import('@/features/strategies/pages/StrategyCatalogPage'));
const StrategyConfigsPage = lazy(() => import('@/features/strategies/pages/StrategyConfigsPage'));
const StrategyConfigDetailPage = lazy(
  () => import('@/features/strategies/pages/StrategyConfigDetailPage'),
);
const StrategyBuilderPage = lazy(() => import('@/features/strategies/pages/StrategyBuilderPage'));

// Backtests
const BacktestListPage = lazy(() => import('@/features/backtests/pages/BacktestListPage'));
const BacktestDetailPage = lazy(() => import('@/features/backtests/pages/BacktestDetailPage'));
const BacktestComparePage = lazy(() => import('@/features/backtests/pages/BacktestComparePage'));

// Portfolio & Trading
const PortfolioPage = lazy(() =>
  import('@/features/portfolio/pages/PortfolioPage').then((m) => ({ default: m.PortfolioPage })),
);
const OrdersPage = lazy(() => import('@/features/orders/pages/OrdersPage'));
const PositionsPage = lazy(() => import('@/features/portfolio/pages/PositionsPage'));

// Alerts (LOK-1106)
const AlertsPage = lazy(() => import('@/features/alerts/pages/AlertsPage'));

// Wallets
const WalletsPage = lazy(() => import('@/features/wallets/pages/WalletsPage'));
const WalletDetailPage = lazy(() => import('@/features/wallets/pages/WalletDetailPage'));

// Risk & Safety (LOK-1027: merged)
const RiskSafetyPage = lazy(() =>
  import('@/features/risk/pages/RiskSafetyPage').then((m) => ({
    default: m.RiskSafetyPage,
  })),
);

// Safety — first-class nav (LOK-1108, un-merged from /risk?tab=safety)
const SafetyPage = lazy(() => import('@/features/safety/pages/SafetyPage'));

// System
const SystemMonitoringPage = lazy(() =>
  import('@/features/system/pages/SystemMonitoringPage').then((m) => ({
    default: m.SystemMonitoringPage,
  })),
);

// Market Data
const MarketDataPage = lazy(() => import('@/features/market-data/pages/MarketDataPage'));

// Signals
const SignalsPage = lazy(() => import('@/features/signals/pages/SignalsPage'));

// Settings & Glossary
const SettingsPage = lazy(() =>
  import('@/features/settings/pages/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const GlossaryPage = lazy(() => import('@/features/glossary/pages/GlossaryPage'));

// Admin
const UsersPage = lazy(() =>
  import('@/features/admin/pages/UsersPage').then((m) => ({ default: m.UsersPage })),
);
const UserDetailPage = lazy(() =>
  import('@/features/admin/pages/UserDetailPage').then((m) => ({ default: m.UserDetailPage })),
);
const AuditPage = lazy(() => import('@/features/admin/pages/AuditPage'));
const SecurityEventsPage = lazy(() => import('@/features/admin/pages/SecurityEventsPage'));
const FeatureFlagsPage = lazy(() => import('@/features/admin/pages/FeatureFlagsPage'));
const DLQPage = lazy(() => import('@/features/admin/pages/DLQPage'));

// Dev-only — gated by `import.meta.env.DEV` so production builds tree-shake.
const UiGalleryPage = import.meta.env.DEV
  ? lazy(() => import('@/features/internal/ui-gallery/UiGalleryPage'))
  : null;

function SuspensePage({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>{children}</Suspense>
    </ErrorBoundary>
  );
}

export const router = createBrowserRouter([
  // ── Public routes ──────────────────────────────────────────────────────────
  {
    path: '/',
    element: (
      <SuspensePage>
        <LandingPage />
      </SuspensePage>
    ),
  },
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/demo',
    element: (
      <SuspensePage>
        <DemoPage />
      </SuspensePage>
    ),
  },
  {
    path: '/forgot-password',
    element: (
      <SuspensePage>
        <ForgotPasswordPage />
      </SuspensePage>
    ),
  },
  {
    path: '/reset-password',
    element: (
      <SuspensePage>
        <ResetPasswordPage />
      </SuspensePage>
    ),
  },

  // ── Protected routes ───────────────────────────────────────────────────────
  {
    element: <AuthGuard />,
    children: [
      // Onboarding wizard (no AppLayout — owns its own chrome).
      // Default redirect → first-uncompleted step inside the wizard.
      {
        path: 'onboarding',
        element: (
          <SuspensePage>
            <OnboardingPage />
          </SuspensePage>
        ),
      },
      {
        path: 'onboarding/:step',
        element: (
          <SuspensePage>
            <OnboardingPage />
          </SuspensePage>
        ),
      },

      // Authenticated app shell. OnboardingGuard redirects fresh tenants
      // to /onboarding/welcome; otherwise passes through to the AppLayout.
      {
        element: <OnboardingGuard />,
        children: [
          {
            element: <AppLayout />,
            children: [
              // Dashboard
              {
                path: 'dashboard',
                element: (
                  <SuspensePage>
                    <DashboardPage />
                  </SuspensePage>
                ),
              },

              // Venue Connections (new)
              {
                path: 'venue-connections',
                element: (
                  <SuspensePage>
                    <VenueConnectionsPage />
                  </SuspensePage>
                ),
              },
              {
                // 4-step connect wizard (LOK-1101). Must be declared BEFORE the
                // `:connectionId` dynamic route so `/venue-connections/connect`
                // doesn't get matched as an id.
                path: 'venue-connections/connect',
                element: (
                  <SuspensePage>
                    <VenueConnectWizardPage />
                  </SuspensePage>
                ),
              },
              {
                path: 'venue-connections/:connectionId',
                element: (
                  <SuspensePage>
                    <VenueConnectionDetailPage />
                  </SuspensePage>
                ),
              },
              // Legacy redirect from handoff naming (`/exchanges/connect`)
              {
                path: 'exchanges/connect',
                element: <Navigate to="/venue-connections/connect" replace />,
              },

              // Exchange Accounts (deprecated — keep for backward compat, redirect later)
              {
                path: 'exchange-accounts',
                element: <Navigate to="/venue-connections" replace />,
              },
              {
                path: 'exchange-accounts/:accountId',
                element: <Navigate to="/venue-connections" replace />,
              },

              // LOK-1126 — legacy path redirects so stale internal links and
              // pasted URLs land on the right page instead of the landing.
              {
                path: 'venues',
                element: <Navigate to="/venue-connections" replace />,
              },
              {
                path: 'venues/connect',
                element: <Navigate to="/venue-connections/connect" replace />,
              },
              {
                path: 'venues/:connectionId',
                element: <VenueConnectionParamRedirect />,
              },
              {
                path: 'audit-trail',
                element: <Navigate to="/admin/audit" replace />,
              },

              // Execution Profiles (new)
              {
                path: 'execution-profiles',
                element: (
                  <SuspensePage>
                    <ExecutionProfilesPage />
                  </SuspensePage>
                ),
              },
              {
                path: 'execution-profiles/:profileId',
                element: (
                  <SuspensePage>
                    <ExecutionProfileDetailPage />
                  </SuspensePage>
                ),
              },

              // Bots
              {
                path: 'bots',
                element: (
                  <SuspensePage>
                    <BotListPage />
                  </SuspensePage>
                ),
              },
              {
                path: 'bots/create',
                element: (
                  <SuspensePage>
                    <BotCreatePage />
                  </SuspensePage>
                ),
              },
              // LOK-1126 — legacy `/bots/new` would otherwise be captured by
              // `/bots/:botId` and trigger a misleading "Bot not found".
              {
                path: 'bots/new',
                element: <Navigate to="/bots/create" replace />,
              },
              {
                path: 'bots/:botId',
                element: (
                  <UlidRouteGuard paramName="botId">
                    <SuspensePage>
                      <BotDetailPage />
                    </SuspensePage>
                  </UlidRouteGuard>
                ),
              },

              // Strategies (new)
              {
                path: 'strategies',
                element: (
                  <SuspensePage>
                    <StrategyCatalogPage />
                  </SuspensePage>
                ),
              },
              {
                path: 'strategies/configs',
                element: (
                  <SuspensePage>
                    <StrategyConfigsPage />
                  </SuspensePage>
                ),
              },
              {
                path: 'strategies/configs/:configId',
                element: (
                  <SuspensePage>
                    <StrategyConfigDetailPage />
                  </SuspensePage>
                ),
              },
              {
                path: 'strategies/builder/new',
                element: (
                  <SuspensePage>
                    <StrategyBuilderPage />
                  </SuspensePage>
                ),
              },
              {
                path: 'strategies/builder/fork/:templateId',
                element: (
                  <SuspensePage>
                    <StrategyBuilderPage />
                  </SuspensePage>
                ),
              },
              {
                path: 'strategies/builder/:configId',
                element: (
                  <SuspensePage>
                    <StrategyBuilderPage />
                  </SuspensePage>
                ),
              },

              // Backtests
              {
                path: 'backtests',
                element: (
                  <SuspensePage>
                    <BacktestListPage />
                  </SuspensePage>
                ),
              },
              // LOK-1126 — literal path must come BEFORE the `:runId` param.
              {
                path: 'backtests/compare',
                element: (
                  <SuspensePage>
                    <BacktestComparePage />
                  </SuspensePage>
                ),
              },
              {
                path: 'backtests/:runId',
                element: (
                  <SuspensePage>
                    <BacktestDetailPage />
                  </SuspensePage>
                ),
              },

              // Portfolio & Trading
              {
                path: 'portfolio',
                element: (
                  <SuspensePage>
                    <PortfolioPage />
                  </SuspensePage>
                ),
              },
              {
                path: 'orders',
                element: (
                  <SuspensePage>
                    <OrdersPage />
                  </SuspensePage>
                ),
              },
              {
                path: 'positions',
                element: (
                  <SuspensePage>
                    <PositionsPage />
                  </SuspensePage>
                ),
              },

              // Alerts (LOK-1106)
              {
                path: 'alerts',
                element: (
                  <SuspensePage>
                    <AlertsPage />
                  </SuspensePage>
                ),
              },

              // Wallets (new)
              {
                path: 'wallets',
                element: (
                  <SuspensePage>
                    <WalletsPage />
                  </SuspensePage>
                ),
              },
              {
                path: 'wallets/:walletId',
                element: (
                  <SuspensePage>
                    <WalletDetailPage />
                  </SuspensePage>
                ),
              },

              // Signals
              {
                path: 'signals',
                element: (
                  <SuspensePage>
                    <SignalsPage />
                  </SuspensePage>
                ),
              },

              // Risk (LOK-1027 / LOK-1107). Safety used to be tabbed here.
              {
                path: 'risk',
                element: (
                  <SuspensePage>
                    <RiskSafetyPage />
                  </SuspensePage>
                ),
              },
              // Safety — first-class route (LOK-1108)
              {
                path: 'safety',
                element: (
                  <SuspensePage>
                    <SafetyPage />
                  </SuspensePage>
                ),
              },

              // System Monitoring
              {
                path: 'system',
                element: (
                  <SuspensePage>
                    <SystemMonitoringPage />
                  </SuspensePage>
                ),
              },

              // Market Data (new)
              {
                path: 'market-data',
                element: (
                  <SuspensePage>
                    <MarketDataPage />
                  </SuspensePage>
                ),
              },

              // Settings & Glossary
              {
                path: 'settings',
                element: (
                  <SuspensePage>
                    <SettingsPage />
                  </SuspensePage>
                ),
              },
              {
                path: 'glossary',
                element: (
                  <SuspensePage>
                    <GlossaryPage />
                  </SuspensePage>
                ),
              },

              // Admin
              {
                path: 'admin',
                element: (
                  <SuspensePage>
                    <UsersPage />
                  </SuspensePage>
                ),
              },
              {
                path: 'admin/audit',
                element: (
                  <SuspensePage>
                    <AuditPage />
                  </SuspensePage>
                ),
              },
              {
                path: 'admin/security-events',
                element: (
                  <SuspensePage>
                    <SecurityEventsPage />
                  </SuspensePage>
                ),
              },
              {
                path: 'admin/feature-flags',
                element: (
                  <SuspensePage>
                    <FeatureFlagsPage />
                  </SuspensePage>
                ),
              },
              {
                path: 'admin/dlq',
                element: (
                  <SuspensePage>
                    <DLQPage />
                  </SuspensePage>
                ),
              },
              // LOK-1126 — legacy `/admin/security` (the audit named the page
              // wrong). Must come before `:userId`.
              {
                path: 'admin/security',
                element: <Navigate to="/admin/security-events" replace />,
              },
              {
                path: 'admin/users/:userId',
                element: (
                  <UlidRouteGuard paramName="userId">
                    <SuspensePage>
                      <UserDetailPage />
                    </SuspensePage>
                  </UlidRouteGuard>
                ),
              },
            ],
          },
        ],
      },
    ],
  },
  // Dev-only UI gallery (LOK-1098). Conditionally registered so production
  // builds neither expose the route nor pull the feature module into the bundle.
  ...(UiGalleryPage
    ? [
        {
          path: '/internal/ui-gallery',
          element: (
            <SuspensePage>
              <UiGalleryPage />
            </SuspensePage>
          ),
        },
      ]
    : []),

  // LOK-1126 — explicit 404 instead of silently dropping to landing.
  {
    path: '*',
    element: <NotFoundPage />,
  },
]);
